import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { EnterpriseScreenShell, actionButtonFactory, useStandardConfirms, type FormModalMode } from 'components/enterprise-shell';
import type { ActionButton } from 'components/modals';
import { SyutGrid, SyutSelect, type ActionMenuItem, type SyutGridColumn } from 'components/ui';
import GridLoadingSkeleton from 'components/common/GridLoadingSkeleton';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import notificationService from 'services/notificationService';
import { chargeTypeService } from 'api/services/chargeTypeService';
import {
  REVENUE_GROUP_OPTIONS,
  revenueGroupLabel,
  supplyNatureLabel,
  chargeNatureLabel,
  type ChargeRevenueGroup,
  type ChargeType,
} from 'api/types/chargeType.types';
import ChargeTypeFormModal from './ChargeTypeFormModal';

/**
 * ============================================================================
 * CHARGE TYPES — what a folio can be charged for
 * ============================================================================
 * Tenant-wide list (Room, Restaurant, Laundry…). Tax per property is set on
 * Charge Tax Mappings. A new type is worth adding only when its tax, who pays,
 * or its accounting differs — room types and outlets are not charge types; the
 * folio line already carries the room and the outlet. System types are shipped
 * with CRS and can only have their description and order changed. A type in use
 * is deactivated rather than deleted.
 * ============================================================================
 */

interface Props {
  tabId?: string;
  moduleIcon?: string;
  moduleName?: string;
  allowedActions?: string[];
}

interface EditorState {
  mode: FormModalMode;
  record: ChargeType | null;
}

const ChargeTypesScreen: React.FC<Props> = ({ tabId, moduleName = 'Charge Types', allowedActions = [] }) => {
  const canAdd = allowedActions.includes('add');
  const canEdit = allowedActions.includes('edit');
  const canDelete = allowedActions.includes('delete');

  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();
  const { confirmDelete, ConfirmDialogPortal } = useStandardConfirms();

  // ---- filters and server-side list ---------------------------------------------------------
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [revenueGroup, setRevenueGroup] = useState<ChargeRevenueGroup | ''>('');
  const [includeInactive, setIncludeInactive] = useState(true);
  const [rows, setRows] = useState<ChargeType[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await chargeTypeService.getAll({
        searchTerm: debouncedSearch || undefined,
        revenueGroup: revenueGroup || undefined,
        includeInactive,
        page,
        pageSize,
      });
      setRows(res.data);
      setTotalCount(res.pagination.totalItems);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'Failed to load charge types.');
    } finally {
      setLoading(false);
      setReady(true);
    }
  }, [debouncedSearch, revenueGroup, includeInactive, page, pageSize]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { setPage(1); }, [debouncedSearch, revenueGroup, includeInactive]);

  // ---- editor ---------------------------------------------------------------------------------
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [createInstanceId, setCreateInstanceId] = useState('1');

  const restoreIfMinimized = useCallback((modalId: string | null): boolean => {
    if (!modalId || !isMinimized(modalId)) return false;
    const modal = getMinimizedModal(modalId);
    dismissMinimized(modalId);
    modal?.onRestore?.();
    return true;
  }, [isMinimized, getMinimizedModal, dismissMinimized]);

  const openCreate = useCallback(() => {
    let next = 1;
    while (tabId && isMinimized(`${tabId}-form-modal-create-${next}`)) next += 1;
    setCreateInstanceId(String(next));
    setEditor({ mode: 'create', record: null });
  }, [tabId, isMinimized]);

  const openFor = useCallback((mode: FormModalMode, record: ChargeType) => {
    if (restoreIfMinimized(tabId ? `${tabId}-form-modal-${mode}-${record.id}` : null)) return;
    setEditor({ mode, record });
  }, [tabId, restoreIfMinimized]);

  const handleRestoreModal = useCallback(async (mode: FormModalMode, instanceId?: string, recordCode?: string) => {
    if (instanceId) setCreateInstanceId(instanceId);
    if (mode === 'create' || !recordCode) { setEditor({ mode, record: null }); return; }
    let record = rows.find((r) => r.id === recordCode) ?? null;
    if (!record) {
      try { record = (await chargeTypeService.getById(recordCode)).data ?? null; } catch { record = null; }
    }
    if (!record) { notificationService.toast.error('The charge type could not be loaded. Refresh and try again.'); return; }
    setEditor({ mode, record });
  }, [rows]);

  const handleDelete = useCallback(async (record: ChargeType) => {
    const ok = await confirmDelete({
      resourceLabel: `charge type ${record.chargeCode} — ${record.chargeName}`,
      message: `Delete ${record.chargeName}? Its code ${record.chargeCode} is kept and cannot be reused. A charge type already used on a folio or a tax rule cannot be deleted — deactivate it instead.`,
    });
    if (!ok) return;
    try {
      await chargeTypeService.delete(record.id, record.rowVersion);
      notificationService.toast.success(`${record.chargeName} deleted.`);
      void load();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'Delete failed.');
    }
  }, [confirmDelete, load]);

  // ---- grid -----------------------------------------------------------------------------------
  const columns = useMemo<SyutGridColumn<ChargeType>[]>(() => [
    {
      id: 'chargeCode', header: 'Code', accessorKey: 'chargeCode', size: 140,
      cell: ({ row }) => <span style={{ fontFamily: 'monospace' }}>{row.original.chargeCode}</span>,
    },
    {
      id: 'chargeName', header: 'Name', accessorKey: 'chargeName', size: 200,
      cell: ({ row }) => (
        <span>
          {row.original.chargeName}
          {row.original.isSystem && <span className="badge badge-info" style={{ fontSize: '0.65rem', marginLeft: '0.4rem' }} title="Shipped with CRS: only description and order can change">System</span>}
        </span>
      ),
    },
    { id: 'revenueGroup', header: 'Revenue group', accessorKey: 'revenueGroup', size: 150, cell: ({ row }) => revenueGroupLabel(row.original.revenueGroup) },
    { id: 'isGuestBillable', header: 'Billed', accessorKey: 'isGuestBillable', size: 100, cell: ({ row }) => (row.original.isGuestBillable ? 'Yes' : 'Internal') },
    { id: 'allowManualPost', header: 'Manual post', accessorKey: 'allowManualPost', size: 110, cell: ({ row }) => (row.original.allowManualPost ? 'Yes' : 'No') },
    { id: 'supplyNature', header: 'Place of supply', accessorKey: 'supplyNature', size: 170, cell: ({ row }) => supplyNatureLabel(row.original.supplyNature) },
    { id: 'chargeNature', header: 'Nature', accessorKey: 'chargeNature', size: 160, cell: ({ row }) => chargeNatureLabel(row.original.chargeNature) },
    { id: 'displayOrder', header: 'Order', accessorKey: 'displayOrder', size: 80 },
    {
      id: 'isActive', header: 'Status', accessorKey: 'isActive', size: 100,
      cell: ({ row }) => <span className={`badge badge-${row.original.isActive ? 'active' : 'inactive'}`}>{row.original.isActive ? 'Active' : 'Inactive'}</span>,
    },
    { id: 'description', header: 'Description', accessorKey: 'description', size: 280, cell: ({ row }) => row.original.description || '—' },
  ], []);

  const getRowActions = useCallback((record: ChargeType): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [];
    if (canEdit) items.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={18} />, onClick: () => openFor('edit', record) });
    if (canAdd) items.push({ id: 'copy', label: 'Copy', icon: <IconSystem.copy size={18} />, onClick: () => openFor('copy', record) });
    if (canDelete && !record.isSystem) {
      items.push({ id: 'delete', label: 'Delete', icon: <IconSystem.trash size={18} />, variant: 'danger', onClick: () => { void handleDelete(record); } });
    }
    return items;
  }, [canAdd, canEdit, canDelete, openFor, handleDelete]);

  const actionButtons: ActionButton[] = [
    { ...actionButtonFactory.add(openCreate, 'Add charge type'), visible: canAdd },
    actionButtonFactory.refresh(() => { void load(); }),
  ];

  return (
    <EnterpriseScreenShell
      title="Charge Types"
      description="What a folio can be charged for. Tax per property is set on Charge Tax Mappings."
      icon={<IconSystem.tag size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
    >
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        <div className="form-group" style={{ minWidth: 240, marginBottom: 0 }}>
          <label className="form-label">Search</label>
          <input
            type="text"
            className="form-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Code, name or description"
          />
        </div>
        <div style={{ minWidth: 200 }}>
          <SyutSelect
            label="Revenue group"
            options={REVENUE_GROUP_OPTIONS}
            value={revenueGroup}
            onChange={(v) => setRevenueGroup(((v as string) || '') as ChargeRevenueGroup | '')}
            placeholder="All groups"
            isClearable
            size="sm"
          />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', paddingBottom: '0.4rem' }}>
          <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} />
          Show inactive
        </label>
      </div>

      {!ready ? (
        <div className="data-grid-container"><GridLoadingSkeleton rows={8} columns={6} /></div>
      ) : (
        <div className="data-grid-container">
          <SyutGrid
            data={rows}
            columns={columns}
            enablePagination
            manualPagination
            totalCount={totalCount}
            currentPage={page - 1}
            pageSize={pageSize}
            onPageChange={(pageIndex) => setPage(pageIndex + 1)}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
            isLoading={loading}
            rowActions={getRowActions}
            emptyMessage="No charge types match"
          />
        </div>
      )}

      {editor && (
        <ChargeTypeFormModal
          isOpen
          mode={editor.mode}
          record={editor.record}
          tabId={tabId}
          moduleName={moduleName}
          createInstanceId={createInstanceId}
          onClose={() => setEditor(null)}
          onSaved={() => { setEditor(null); void load(); }}
          onRestoreModal={handleRestoreModal}
        />
      )}

      <ConfirmDialogPortal />
    </EnterpriseScreenShell>
  );
};

export default ChargeTypesScreen;
